import {
  Component,
  ElementRef,
  HostBinding,
  HostListener,
  ViewChild,
  Input,
  OnChanges,
  OnInit,
  OnDestroy,
  AfterViewInit,
  SimpleChanges,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { CanvasService } from "../../services/canvas.service";
import { ElementFactoryService } from "../../services/element-factory.service";
import { ImageUrlService } from "../../services/image-url.service";
import {
  AnyElement,
  Position,
  Slide,
  TableCellSelection,
} from "../../types/canvas.types";
import { CanvasElementComponent } from "../canvas-element/canvas-element.component";
import { Subscription } from "rxjs";

@Component({
  selector: "lib-canvas",
  template: `
    <div
      class="canvas-container"
      #canvasContainer
      [class.readonly-mode]="readonly"
    >
      <div
        class="canvas-viewport"
        [style.width.px]="canvasViewportWidth"
        [style.height.px]="canvasViewportHeight"
      >
        <div
          class="canvas-area"
          [class.drag-over]="isDragOver && !readonly"
          [class.readonly]="readonly"
          [style.background]="getSlideBackground()"
          [style.transform]="canvasTransform"
          [style.transform-origin]="canvasTransformOrigin"
          (drop)="onDrop($event)"
          (dragover)="onDragOver($event)"
          (dragleave)="onDragLeave($event)"
          (click)="onCanvasClick($event)"
        >
          <lib-canvas-element
            *ngFor="let element of elements; trackBy: trackElement"
            [element]="element"
            [isSelected]="selectedElement?.id === element.id"
            (elementSelect)="selectElement($event)"
            (elementUpdate)="updateElement($event.id, $event.updates)"
            (elementDelete)="deleteElement($event)"
            (tableCellSelect)="onTableCellSelect($event)"
          >
          </lib-canvas-element>
        </div>
      </div>
    </div>
  `,
  styles: [
    `
      :host {
        /* CSS variables for consistent styling */
        --canvas-primary: #232459;
        --canvas-bg-tertiary: #f1f5f9;
        --canvas-gray-100: #f3f4f6;
        --canvas-space-4: 1rem;
        --canvas-space-5: 1.25rem;
        --canvas-radius-lg: 0.75rem;
        --canvas-transition: 200ms ease;
        --canvas-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
        --canvas-shadow-lg: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
        display: block;
        width: 100%;
        height: 100%;
      }

      :host.readonly-mode {
        height: auto;
      }

      .canvas-container {
        width: 100%;
        height: 100%;
        overflow: auto;
        background: var(--canvas-bg-tertiary);
        position: relative;
        padding: var(--canvas-space-4);
        display: flex;
        align-items: flex-start;
        justify-content: flex-start;
        box-sizing: border-box;
        gap: var(--canvas-space-4);
      }

      .canvas-container.readonly-mode {
        overflow: visible;
        padding: 0;
        background: transparent;
        justify-content: center;
        align-items: center;
        height: auto;
        min-height: 100%;
        gap: 0;
      }

      .canvas-viewport {
        position: relative;
        flex: 0 0 auto;
        width: 800px;
        height: 600px;
        display: flex;
        align-items: flex-start;
        justify-content: flex-start;
        transform-origin: top left;
        max-width: 100%;
        max-height: 100%;
        box-sizing: border-box;
      }

      .canvas-container.readonly-mode .canvas-viewport {
        align-items: center;
        justify-content: center;
        overflow: visible;
        padding: 0;
      }

      .canvas-area {
        width: 800px;
        height: 600px;
        background: white;
        position: relative;
        border-radius: var(--canvas-radius-lg);
        box-shadow: var(--canvas-shadow-lg);
        transition: all var(--canvas-transition);
        overflow: hidden;
        flex: 0 0 auto;
      }

      .canvas-container.readonly-mode .canvas-area {
        box-shadow: 0 0 0 1px rgba(148, 163, 184, 0.9);
        border-radius: 8px;
        border: none;
      }

      .canvas-area:hover {
        box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1),
          0 10px 10px -5px rgba(0, 0, 0, 0.04);
      }

      .drag-over {
        background: rgba(35, 36, 89, 0.05);
        border: 2px dashed var(--canvas-primary);
      }

      .readonly {
        pointer-events: none;
        user-select: none;
      }

      .readonly .canvas-element {
        cursor: default !important;
      }

      .canvas-container::-webkit-scrollbar {
        width: 10px;
        height: 10px;
      }

      .canvas-container::-webkit-scrollbar-track {
        background: #e2e8f0;
        border-radius: 6px;
      }

      .canvas-container::-webkit-scrollbar-thumb {
        background: rgba(35, 36, 89, 0.45);
        border-radius: 6px;
      }

      .canvas-container::-webkit-scrollbar-thumb:hover {
        background: rgba(35, 36, 89, 0.65);
      }
    `,
  ],
})
export class CanvasComponent
  implements OnInit, OnChanges, OnDestroy, AfterViewInit
{
  @ViewChild("canvasContainer", { static: true }) canvasContainer!: ElementRef;
  @Input() readonly: boolean = false;
  @Input() slideData: Slide | null = null;
  @HostBinding("class.readonly-mode") get hostReadonlyMode(): boolean {
    return this.readonly;
  }

  elements: AnyElement[] = [];
  selectedElement: AnyElement | null = null;
  isDragOver = false;
  zoomLevel = 1;
  readonly baseWidth = this.canvasService.canvasDimensions.width;
  readonly baseHeight = this.canvasService.canvasDimensions.height;
  private useServiceData = true;
  private subscriptions = new Subscription();
  private applyingAutoFit = false;
  private autoFitActive = false;
  private resizeObserver?: ResizeObserver;

  constructor(
    private canvasService: CanvasService,
    private elementFactory: ElementFactoryService,
    private imageUrlService: ImageUrlService
  ) {
    this.subscriptions.add(
      this.canvasService.currentSlide$.subscribe((slide) => {
        if (this.useServiceData) {
          this.elements = slide?.elements || [];
        }
      })
    );

    this.subscriptions.add(
      this.canvasService.selectedElement$.subscribe((element) => {
        if (this.useServiceData) {
          this.selectedElement = element;
        }
      })
    );

    this.subscriptions.add(
      this.canvasService.zoom$.subscribe((level) => {
        this.zoomLevel = level;
        if (!this.applyingAutoFit) {
          this.autoFitActive = false;
        }
      })
    );

    this.subscriptions.add(
      this.canvasService.fitRequest$.subscribe(() => {
        this.enableAutoFit();
      })
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Handle slideData input changes
    if (changes["slideData"] && this.slideData) {
      this.useServiceData = false;
      this.elements = (this.slideData.elements || []).map((element) => ({
        ...element,
        position: { ...element.position },
        size: { ...element.size },
      }));
      this.selectedElement = null; // Clear selection for preview mode
    } else if (changes["slideData"] && !this.slideData) {
      this.useServiceData = true;
    }
  }

  ngOnInit(): void {}

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.applyAutoFit();
      if (this.readonly) {
        this.enableAutoFit();
      }
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.resizeObserver?.disconnect();
  }

  get canvasTransform(): string {
    return `scale(${this.zoomLevel})`;
  }

  get canvasTransformOrigin(): string {
    return this.readonly ? "center center" : "top left";
  }

  get canvasViewportWidth(): number {
    return Math.max(this.baseWidth * this.zoomLevel, 1);
  }

  get canvasViewportHeight(): number {
    return Math.max(this.baseHeight * this.zoomLevel, 1);
  }

  @HostListener("window:resize")
  onWindowResize(): void {
    if (this.autoFitActive) {
      this.applyAutoFit();
    }
  }

  @HostListener("document:keydown", ["$event"])
  onKeyDown(event: KeyboardEvent): void {
    if (this.readonly || !this.selectedElement) {
      return;
    }

    if (event.key !== "Delete" && event.key !== "Backspace") {
      return;
    }

    const target = event.target as HTMLElement | null;
    if (target) {
      const tagName = target.tagName?.toLowerCase();
      const isFormField = tagName === "input" || tagName === "textarea";
      if (isFormField || target.isContentEditable) {
        return;
      }
    }

    event.preventDefault();
    this.deleteElement(this.selectedElement.id);
  }

  onDrop(event: DragEvent): void {
    if (this.readonly) return;

    event.preventDefault();
    this.isDragOver = false;

    const data = event.dataTransfer?.getData("application/json");
    if (!data) return;

    const dragData = JSON.parse(data);
    const target = (event.currentTarget || event.target) as HTMLElement;
    const rect = target.getBoundingClientRect();
    const position: Position = {
      x: (event.clientX - rect.left) / this.zoomLevel,
      y: (event.clientY - rect.top) / this.zoomLevel,
    };

    let element: AnyElement;

    switch (dragData.type) {
      case "image":
        // Resolve the image path back to full URL
        const fullImageUrl = this.imageUrlService.resolveImageUrl(dragData.url);
        element = this.elementFactory.createImageElement(
          position,
          fullImageUrl
        );
        break;
      case "shape":
        element = this.elementFactory.createShapeElement(
          position,
          dragData.shapeType
        );
        break;
      case "text":
        element = this.elementFactory.createTextElement(position);
        break;
      default:
        return;
    }

    this.canvasService.addElement(element);
  }

  onDragOver(event: DragEvent): void {
    if (this.readonly) return;
    event.preventDefault();
    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent): void {
    if (this.readonly) return;
    this.isDragOver = false;
  }

  onCanvasClick(event: MouseEvent): void {
    if (this.readonly) return;
    if (event.target === event.currentTarget) {
      this.canvasService.selectElement(null);
    }
  }

  selectElement(element: AnyElement): void {
    if (this.readonly) return;
    this.canvasService.selectElement(element);
  }

  updateElement(elementId: string, updates: Partial<AnyElement>): void {
    if (this.readonly) return;
    this.canvasService.updateElement(elementId, updates);
  }

  deleteElement(elementId: string): void {
    if (this.readonly) return;
    this.canvasService.deleteElement(elementId);
  }

  onTableCellSelect(selection: TableCellSelection | null): void {
    if (this.readonly) return;
    this.canvasService.selectTableCell(selection);
  }

  trackElement(index: number, element: AnyElement): string {
    return element.id;
  }

  getSlideBackground(): string {
    if (this.slideData?.background) {
      return this.slideData.background;
    }
    // Default to white if no slide data or background
    return "white";
  }

  private applyAutoFit(): void {
    this.applyingAutoFit = true;
    this.fitCanvasToContainer();
    this.applyingAutoFit = false;
  }

  private fitCanvasToContainer(): void {
    const containerEl = this.canvasContainer?.nativeElement as HTMLElement;
    if (!containerEl) {
      return;
    }

    const viewportEl = containerEl.closest(
      ".canvas-designer__canvas-area"
    ) as HTMLElement | null;

    const targetEl = viewportEl ?? containerEl;
    const styles = window.getComputedStyle(targetEl);
    const paddingX =
      parseFloat(styles.paddingLeft || "0") +
      parseFloat(styles.paddingRight || "0");
    const paddingY =
      parseFloat(styles.paddingTop || "0") +
      parseFloat(styles.paddingBottom || "0");

    const availableWidth = Math.max(0, targetEl.clientWidth - paddingX);
    const availableHeight = Math.max(0, targetEl.clientHeight - paddingY);

    const newZoom = this.canvasService.fitZoomToViewport(
      availableWidth,
      availableHeight
    );

    if (!isFinite(newZoom) || newZoom <= 0) {
      this.canvasService.resetZoom();
    }
  }

  private enableAutoFit(): void {
    if (!this.autoFitActive) {
      this.autoFitActive = true;
      this.setupResizeObserver();
    }
    this.applyAutoFit();
  }

  private setupResizeObserver(): void {
    if (this.resizeObserver || typeof ResizeObserver === "undefined") {
      return;
    }

    const containerEl = this.canvasContainer?.nativeElement as HTMLElement;
    if (!containerEl) {
      return;
    }

    this.resizeObserver = new ResizeObserver(() => {
      if (this.autoFitActive) {
        this.applyAutoFit();
      }
    });

    this.resizeObserver.observe(containerEl);
  }
}
